import React, { useContext, useEffect, useState } from "react"
import moment from "moment"
import ServiceCard from "./ServiceCard"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { ServiceRequests } from "../../api/app.service"
import { SvgDraggable, SvgPlus } from "../icons/"
import { UserCtx } from "../../context/userContext"
import { MeetingRequests } from "../../api/app.service"
import { Meeting } from "../../types/meetingTypes"
import { useToast } from "../../hooks/useToast"

interface Props {
  services: any
  setUpdateService: any
  getServices: any
  showComponent: any
  setShowArchivedServices: any
  setIsGroupService: any
  showGroupVsIndividualSelector: any
  setShowDuplicateService: any
}

const ActiveServices: React.FC<Props> = ({
  services,
  setUpdateService,
  getServices,
  showComponent,
  setShowArchivedServices,
  setIsGroupService,
  showGroupVsIndividualSelector,
  setShowDuplicateService,
}) => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const activeServices = services.filter(
    (service: any) => service.service_details.status === "active"
  )
  const [orderedServices, setOrderedServices] = useState(activeServices)
  const [activeDate, setActiveDate] = useState<Date>(
    moment(new Date()).startOf("day").tz(user?.timezone?.value)?.toDate()
  )

  const reorderClientServices = (ids: number[]) => {
    ServiceRequests.reorderClientServices({
      service_ids: ids,
    })
      .then(() => {})
      .catch((ex) => {
        console.log(ex)
      })
  }

  const onDragEnd = (result: any) => {
    const { destination, source } = result

    if (!destination) return

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return
    }

    const items = Array.from(orderedServices)
    const [reorderedItem] = items.splice(source.index, 1)
    items.splice(destination.index, 0, reorderedItem)

    setOrderedServices(items)

    items.forEach((item: any, index) => {})

    reorderClientServices(items.map((item: any) => item.service_id))
  }

  const calcDuration = (endTime: any, startTime: any) => {
    const calculated = moment
      .utc(endTime)
      .diff(moment.utc(startTime), "minutes")
    let duration
    if (calculated >= 1) {
      duration = calculated
    } else {
      duration = 1
    }
    return duration
  }

  const [serviceMeetingsMap, setServiceMeetingsMap] = useState<{
    [key: string]: Meeting[]
  }>({})

  const getMeetings = async () => {
    if (!user) return

    const startOfActiveDate = moment(activeDate)
      ?.startOf("day")
      ?.utc()
      ?.tz(user?.timezone?.value)
      ?.toISOString()
    const endOfSixMonths = moment(activeDate)
      ?.add(6, "month")
      ?.endOf("day")
      ?.utc()
      ?.tz(user.timezone.value)
      ?.toISOString()

    try {
      const data = await MeetingRequests.getMeetings({
        user_id: parseInt(user.user_id),
        profile_id: user.activeProfileId,
        from_date: startOfActiveDate,
        to_date: endOfSixMonths,
      })

      const meetingsMap: { [key: string]: Meeting[] } = {}

      activeServices.forEach((service: any) => {
        const matchingMeetings = data.meetings
          .filter((m) => m.stripe_product_id === `prod_${service.service_id}`)
          .map((m) => ({
            ...m,
            startTime: moment
              .utc(m.start_time)
              .tz(user.timezone.value)
              .toDate(),
            endTime: m.end_time
              ? moment.utc(m.end_time).tz(user.timezone.value).toDate()
              : null,
            duration: calcDuration(m.end_time, m.start_time),
          }))
        meetingsMap[service.service_id] = matchingMeetings
      })

      setServiceMeetingsMap(meetingsMap)
    } catch (ex: any) {
      toast.error(ex.response?.data?.message || "Failed to fetch meetings")
    }
  }

  useEffect(() => {
    getMeetings()
  }, [])

  return (
    <>
      {activeServices.length > 0 ? (
        <div className="flex flex-wrap items-center justify-center w-full gap-[24px]">
          {" "}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="services">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="grid grid-cols-1 lg:grid-cols-2 justify-items-center gap-6 last:mb-[80px]"
                >
                  {" "}
                  {orderedServices.map((service: any, index: number) => (
                    <Draggable
                      draggableId={String(service.service_id)}
                      index={index}
                      key={service.service_id}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`${
                            snapshot.isDragging ? "opacity-50" : ""
                          }`}
                        >
                          <div
                            {...provided.dragHandleProps}
                            className="px-2 py-4 opacity-50 hover:opacity-100 transition-all inline-flex"
                          >
                            <SvgDraggable />
                          </div>

                          <ServiceCard
                            service={service}
                            meetings={
                              serviceMeetingsMap[service.service_id] || []
                            }
                            setUpdateService={setUpdateService}
                            getServices={getServices}
                            setShowArchivedServices={setShowArchivedServices}
                            showComponent={showComponent}
                            setIsGroupService={setIsGroupService}
                            setShowDuplicateService={setShowDuplicateService}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      ) : (
        <div
          className="flex px-[8%] items-center justify-center h-[399px] border border-1
                    border-grayMist rounded-[16px] bg-[#f8f8f8]"
        >
          <div className="flex-col items-center justify-center gap-[10px]">
            <h2 className="font-bold text-center text-[21px]">
              Create, share, and sell personalized coaching programs quickly and
              easily. Client onboarding is automated, right from the moment they
              sign up. From scheduling sessions to handling payments, everything
              is streamlined for a hassle-free experience.
            </h2>
            <button
              className="btn-primary btn-blue bg-blurple mx-auto mt-[20px] md:mt-[40px]"
              onClick={showGroupVsIndividualSelector}
            >
              Create a Service
              <SvgPlus />
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default ActiveServices
